
import { ORBITAL_ICON_BOX_STYLE } from '@/components/help-center/latest-power-shared';
import type { EverypowerPawnTooltipData } from '@/lib/everypower-data';
import { cn } from '@/lib/utils';
import type { PowerGalleryViewMode } from '@/components/help-center/powers/power-gallery-view';

function PawnTooltipImage({
  src,
  alt,
  viewMode,
  compact = false,
}: {
  src: string;
  alt: string;
  viewMode: PowerGalleryViewMode;
  compact?: boolean;
}) {
  const isDark = viewMode === 'dark';

  return (
    <div
      className={cn(
        'relative h-10 w-10 shrink-0 overflow-hidden rounded-lg',
        isDark ? 'ring-1 ring-blue-900/60' : 'bg-gray-50 ring-1 ring-gray-200'
      )}
      style={isDark ? ORBITAL_ICON_BOX_STYLE : undefined}
    >
      <img
        src={src}
        alt={alt}
        className={cn(
          'absolute left-1/2 top-1/2 -translate-x-[calc(50%-5px)] -translate-y-[calc(50%-2px)] object-contain',
          compact ? 'h-[26px] w-[26px]' : 'h-8 w-8',
          isDark && 'brightness-110 contrast-105 drop-shadow-[0_0_4px_rgba(56,189,248,0.25)]'
        )}
        draggable={false}
      />
    </div>
  );
}

export function EverypowerPawnTooltipBubble({
  data,
  viewMode,
}: {
  data: EverypowerPawnTooltipData;
  viewMode: PowerGalleryViewMode;
}) {
  const isDark = viewMode === 'dark';
  const everypawnSrc = isDark ? data.everypawnDarkSrc : data.everypawnLightSrc;
  const referenceSrc = isDark ? data.referencePawnDarkSrc : data.referencePawnLightSrc;

  return (
    <span
      role="tooltip"
      className={cn(
        'pointer-events-none absolute bottom-full right-0 z-50 mb-1.5 w-[min(240px,calc(100vw-3rem))] rounded-xl px-3 py-2.5 text-left opacity-0 shadow-lg transition-opacity duration-150 group-hover/info:opacity-100 group-focus-within/info:opacity-100',
        isDark
          ? 'border border-blue-900/55 bg-[#0a1450] text-blue-50 shadow-blue-950/60'
          : 'border border-gray-200 bg-white text-gray-700'
      )}
    >
      {referenceSrc ? (
        <div className="mb-2 flex items-center justify-center gap-2">
          <div className="flex flex-col items-center gap-1">
            <PawnTooltipImage
              src={referenceSrc}
              alt={data.powerLabel}
              viewMode={viewMode}
              compact={data.referencePawnCompact}
            />
            <span
              className={cn(
                'font-poppins text-[9px] font-semibold uppercase tracking-wide',
                isDark ? 'text-sky-300/90' : 'text-gray-500'
              )}
            >
              {data.powerLabel}
            </span>
          </div>
          {everypawnSrc ? (
            <>
              <span
                className={cn(
                  'self-center font-poppins text-[10px] font-bold',
                  isDark ? 'text-sky-400/80' : 'text-gray-400'
                )}
                aria-hidden
              >
                →
              </span>
              <div className="flex flex-col items-center gap-1">
                <PawnTooltipImage src={everypawnSrc} alt="Everypower" viewMode={viewMode} />
                <span
                  className={cn(
                    'font-poppins text-[9px] font-semibold uppercase tracking-wide',
                    isDark ? 'text-sky-300/70' : 'text-gray-400'
                  )}
                >
                  Everypower
                </span>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      <span className="block font-inter text-[11px] leading-snug">{data.text}</span>
    </span>
  );
}
